import { UploadedFile } from "@/pages/Catalogue/CatalogueForm/SecondStep/types/types.ts";
import { AutomationDto } from "@/shared/dtos/automation-dto";
import { SourceTypeEnum } from "@/shared/enums/Streaming/source-type-enum";
import { UploadStatus } from "@/shared/enums/upload-status";
import { ArtifactService } from "@/shared/services/artifactService";
import { AutomationService } from "@/shared/services/automationService";
import { DatasetService } from "@/shared/services/datasetService";
import { RootState } from "@/store";
import { PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import artifactUploaderSlice from "./artifactUploaderSlice";
import { extractStreamingId, handleUploadError } from "./uploadHelpers";

export const artifactUploaderActions = artifactUploaderSlice.actions;

export const uploadArtifact = (
	artifact: any,
	uploadedFile: UploadedFile,
	datasetId: string,
	datasetTitle: string,
	artifactDisplayName?: string,
	userEmail?: string,
	userId?: string,
): ThunkAction<void, RootState, unknown, PayloadAction<Promise<void>>> => {
	return async (dispatch) => {
		const displayName = artifactDisplayName || uploadedFile.name;

		try {
			// 1. Iniciar el proceso de upload
			dispatch(
				artifactUploaderActions.uploadStarted({
					id: uploadedFile.name,
					fileName: uploadedFile.name,
					status: UploadStatus.UPLOADING,
					datasetId: datasetId,
				}),
			);

			// 2. Crear el artefacto
			const createResponse = await ArtifactService.createArtifact(artifact);
			artifact.artifactId = createResponse.artifactId;

			// 3. Obtener la URL de MinIO para la subida del fichero
			const uploadUrlResponse: any = await ArtifactService.getUploadUrl(
				datasetId,
				artifact.artifactId,
			);

			// 4. Subir el fichero a MinIO
			await ArtifactService.uploadFileToMinIO(
				uploadUrlResponse?.url,
				uploadedFile.file,
			);

			// 5. Consolidar el fichero en la carpeta del usuario de MinIO
			await DatasetService.consolidate(datasetId, artifact.artifactId);

			// 6. Análisis del artefacto (Cálculo de calidad): ISO_ARTIFACT
			const sessionName = `ISO Artifact - ${displayName}`;
			const sessionDescription = artifact.description || displayName;

			// Crear automatización de ISO_ARTIFACT
			const isoAutomation = new AutomationDto({
				sessionName: sessionName,
				sessionDescription: sessionDescription,
				datasetId: datasetId,
				datasetName: datasetTitle,
				sourceConfiguration: {
					sourceType: SourceTypeEnum.ISO_ARTIFACT,
					structuredDistributionId: artifact.artifactId,
					ownerEmail: userEmail,
					ownerId: userId,
					skipKpiProfiling: false,
				},
				startNow: true,
			});
			const isoResponse = await AutomationService.create(isoAutomation, false);

			const streamingId = extractStreamingId(isoResponse);

			// 7. Marcar como "analizando"
			dispatch(
				artifactUploaderActions.artifactProcessed({
					id: artifact.artifactId,
					datasetId: datasetId,
					fileName: uploadedFile.name,
					status: UploadStatus.ANALYZING,
					streamingId: streamingId,
				}),
			);
		} catch (error) {
			let stage: "creation" | "upload" | "consolidation" | "analysis" =
				"creation";

			if (artifact.artifactId) {
				stage = "upload";

				if (error instanceof Error) {
					if (error.message?.includes("consolidate")) {
						stage = "consolidation";
					} else if (error.message?.includes("quality")) {
						stage = "analysis";
					}
				}
			}

			handleUploadError(
				dispatch,
				artifact,
				uploadedFile.name,
				datasetId,
				displayName,
				error,
				stage,
			);
		}
	};
};
